import { Route, Routes } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Fragment, useEffect } from "react";
import { lineSpinner } from "ldrs";

import GlobalStyles from "./styles/globalStyles";
import "./styles/index.css";

import ProtectedRoute from "./components/utils/PrivateRoute";

import { selectLoggedIn, setLoggedIn } from "./store/slices/auth/slice";
import { refresh } from "./store/slices/auth/asyncThunks";
import { getAdminUserInfo } from "./store/slices/user/asyncThunks";
import { getCategories } from "./store/slices/products/asyncThunks";
import { getBandsForDropdown } from "./store/slices/bands/asyncThunks";

import Homepage from "./screens/Portal/Homepage/HomePage";
import LoginPage from "./screens/Portal/LoginPage/LoginPage";
import ShopPage from "./screens/Portal/ShopPage/ShopPage";
import BandDashboardPage from "./screens/Admin/BandDashboardPage/BandDashboardPage";
import AdminDashboardPage from "./screens/Admin/AdminDashboardPage/AdminDashboardPage";
import ProductPage from "./screens/Portal/ProductPage/ProductPage";
import NewsPage from "./screens/Portal/NewsPage/NewsPage";
import BandsPage from "./screens/Portal/BandsPage/BandsPage";
import AboutPage from "./screens/Portal/AboutPage/AboutPage";
import CartPage from "./screens/Portal/CartPage/CartPage";
import OrderSuccessPage from "./screens/Portal/OrderSuccessPage/OrderSuccessPage";
import AdminAnalyticsPage from "./screens/Admin/AdminAnalyticsPage/AdminAnalyticsPage.";
import AdminReportsPage from "./screens/Admin/AdminReportsPage/AdminReportsPage";
import AdminProductsPage from "./screens/Admin/AdminProductsPage/AdminProductsPage";
import ProductDimensionsPage from "./screens/Admin/ProductDimensionsPage/ProductDimensionsPage";
import AdminCategoryPage from "./screens/Admin/AdminCategoryPage/AdminCategoryPage";
import AdminOrdersPage from "./screens/Admin/AdminOrdersPage/AdminOrdersPage";
import AdminBandsPage from "./screens/Admin/AdminBandsPage/AdminBandsPage";
import AdminNewsPage from "./screens/Admin/AdminNewsPage/AdminNewsPage";
import BandDetailsPage from "./screens/Portal/BandDetailsPage/BandDetailsPage";
import NewsDetailsPage from "./screens/Portal/NewsDetailsPage/NewsDetailsPage";
import EmailVerificationPage from "./components/utils/EmailVerificationPage";
import AdminBandDetailsPage from "./screens/Admin/AdminBandDetailsPage/AdminBandDetailsPage";
import AdminNewsDetailsPage from "./screens/Admin/AdminNewsDetailsPage/AdminNewsDetailsPage";
import { Toaster } from "sonner";
import AdminNewslettersPage from "./screens/Admin/AdminNewslettersPage/AdminNewslettersPage";
import BandOrdersPage from "./screens/Admin/BandOrdersPage.js/BandOrdersPage.js";
import BandProductsPage from "./screens/Admin/BandProductsPage/BandProductsPage";
import AdminSupportPage from "./screens/Admin/AdminSupportPage/AdminSupportPage";
import BandSettings from "./screens/Admin/BandSettings/BandSettings";

lineSpinner.register();

function App() {
  const loggedIn = useSelector(selectLoggedIn);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getCategories())
      .unwrap()
      .then(() => {})
      .catch((err) => console.log(err.message));
    dispatch(getBandsForDropdown())
      .unwrap()
      .then(() => {})
      .catch((err) => console.log(err.message));
  }, []);

  useEffect(() => {
    dispatch(refresh())
      .unwrap()
      .then(() => {
        dispatch(getAdminUserInfo())
          .unwrap()
          .then(() => {})
          .catch((err) => console.log(err.message));
      })
      .catch((error) => {
        dispatch(setLoggedIn({ loggedIn: false }));
      });
  }, []);

  return (
    <Fragment>
      <GlobalStyles />

      <Routes>
        {/* Javne rute */}
        <Route path="/" element={<Homepage />} />
        <Route path="/prodavnica" element={<ShopPage />} />
        <Route path="/proizvod/:id" element={<ProductPage />} />
        <Route path="/novosti" element={<NewsPage />} />
        <Route path="/novost/:newsId" element={<NewsDetailsPage />} />
        <Route path="/bendovi" element={<BandsPage />} />
        <Route path="/o-nama" element={<AboutPage />} />
        <Route path="/korpa" element={<CartPage />} />
        <Route path="/porudzbina-uspesna" element={<OrderSuccessPage />} />
        <Route path="/verify-email" element={<EmailVerificationPage />} />
        <Route path="/login" element={<LoginPage />} />

        {/* Protected routes with role-based access */}
        {loggedIn !== null && (
          <>
            {/* Admin routes - only accessible to admin role */}
            <Route element={<ProtectedRoute allowedRoles={["admin"]} />}>
              <Route
                path="/admin-panel/kontrolna-tabla"
                element={<AdminDashboardPage />}
              />
              <Route
                path="/admin-panel/analitika"
                element={<AdminAnalyticsPage />}
              />
              <Route
                path="/admin-panel/izvestaji"
                element={<AdminReportsPage />}
              />
              <Route
                path="/admin-panel/proizvodi"
                element={<AdminProductsPage />}
              />
              <Route
                path="/admin/product-dimensions/:productId"
                element={<ProductDimensionsPage />}
              />
              <Route
                path="/admin-panel/kategorije"
                element={<AdminCategoryPage />}
              />
              <Route
                path="/admin-panel/porudzbine"
                element={<AdminOrdersPage />}
              />
              <Route path="/admin-panel/bendovi" element={<AdminBandsPage />} />
              <Route path="/admin-panel/novosti" element={<AdminNewsPage />} />
              <Route
                path="/admin-panel/newsletters"
                element={<AdminNewslettersPage />}
              />

              <Route
                path="/admin-panel/korisnicka-podrska"
                element={<AdminSupportPage />}
              />

              <Route
                path="/admin-panel/bend/:bandId"
                element={<AdminBandDetailsPage />}
              />
              <Route
                path="/admin-panel/novost/:newsId"
                element={<AdminNewsDetailsPage />}
              />
            </Route>

            {/* Band routes - only accessible to bend role */}
            <Route element={<ProtectedRoute allowedRoles={["band"]} />}>
              <Route
                path="/bend-panel/kontrolna-tabla"
                element={<BandDashboardPage />}
              />
              <Route
                path="/bend-panel/proizvodi"
                element={<BandProductsPage />}
              />
              <Route
                path="/bend-panel/porudzbine"
                element={<BandOrdersPage />}
              />
              <Route
                path="/bend-panel/podesavanja"
                element={<BandSettings />}
              />
            </Route>
          </>
        )}

        {/* Javne rute sa parametrima - moraju biti na kraju zbog React Router prioriteta */}
        <Route path="/bend/:bandId" element={<BandDetailsPage />} />
      </Routes>

      <Toaster
        position="bottom-right"
        richColors
        theme="dark"
        toastOptions={{
          style: {
            background: "linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            color: "white",
          },
        }}
      />
    </Fragment>
  );
}

export default App;
